import type {
  EnrollmentScreen,
  RequirementEntry,
  RequirementMatcher,
  Requirements,
} from "./types";

export function isSatisfied(entry: RequirementEntry | undefined): boolean {
  return !!entry && !entry.unsatisfied;
}

export function isUnsatisfied(entry: RequirementEntry | undefined): boolean {
  return !!entry?.unsatisfied;
}

export function requirementValue<T>(entry: RequirementEntry<T> | undefined): T {
  const value = maybeRequirementValue(entry);
  if (value === undefined) {
    throw new Error("requirement value is not available");
  }
  return value;
}

export function maybeRequirementValue<T>(
  entry: RequirementEntry<T> | undefined,
): T | undefined {
  return entry?.value ?? undefined;
}

export function confirmsData(matches: RequirementMatcher[]): boolean {
  return matches.some((m) => m.mode === "new" || m.mode === "existing");
}

/** A collector screen the customer has already completed (so it's backable). */
export function isSatisfiedCollector(
  screen: EnrollmentScreen,
  requirements: Requirements,
): boolean {
  return screen.matches.some(
    (m) => m.mode === "required" && isSatisfied(requirements[m.key]),
  );
}

export function isActive(
  screen: EnrollmentScreen,
  requirements: Requirements,
  initialRequirements: Requirements,
  toSkip: ReadonlySet<string>,
  conditionRequirements: Requirements = requirements,
): boolean {
  const failsCondition = screen.matches.some(
    (m) => m.mode === "when" && !m.condition(conditionRequirements),
  );
  if (failsCondition) return false;

  const collectsUnsatisfied = screen.matches.some(
    (m) => m.mode === "required" && isUnsatisfied(requirements[m.key]),
  );
  if (collectsUnsatisfied) return true;

  if (screen.matches.some((m) => m.mode === "always")) {
    return !toSkip.has(screen.path);
  }

  if (confirmsData(screen.matches) && !toSkip.has(screen.path)) {
    return screen.matches
      .filter((m) => m.mode !== "required" && m.mode !== "when")
      .every(
        (m) =>
          (m.mode === "new" && isSatisfied(requirements[m.key])) ||
          (m.mode === "existing" && isSatisfied(initialRequirements[m.key])),
      );
  }

  return false;
}

/** The first active screen in declaration order, or the last screen (terminal confirm) if none. */
export function selectActiveScreen(
  screens: EnrollmentScreen[],
  requirements: Requirements,
  initialRequirements: Requirements,
  toSkip: ReadonlySet<string>,
): EnrollmentScreen {
  return (
    screens.find((screen) =>
      isActive(screen, requirements, initialRequirements, toSkip),
    ) ?? screens[screens.length - 1]
  );
}
